import React from "react";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { IManagedChannel } from "../../../services/api/Channel";

interface SortableRowProps {
  channel: IManagedChannel;
  togglingId: number | null;
  onEdit: (channel: IManagedChannel) => void;
  onToggleActive: (channel: IManagedChannel) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({ channel, togglingId, onEdit, onToggleActive }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: channel.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={isDragging ? "channels-management__channel--dragging" : ""}
    >
      <span
        className="channels-management__drag-handle"
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${channel.name}`}
      >
        ⠿
      </span>
      <span className="channels-management__channel-name">{channel.name}</span>
      <span className="channels-management__status">Active</span>
      <button type="button" className="button button--gray" onClick={() => onEdit(channel)}>Edit</button>
      <button
        type="button"
        className="button button--red"
        onClick={() => onToggleActive(channel)}
        disabled={togglingId === channel.id}
      >
        {togglingId === channel.id ? "Saving..." : "Deactivate"}
      </button>
    </li>
  );
};

interface StaticRowProps {
  channel: IManagedChannel;
  togglingId: number | null;
  onEdit: (channel: IManagedChannel) => void;
  onToggleActive: (channel: IManagedChannel) => void;
}

const StaticInactiveRow: React.FC<StaticRowProps> = ({ channel, togglingId, onEdit, onToggleActive }) => (
  <li className="channels-management__channel--inactive">
    <span></span>
    <span className="channels-management__channel-name">{channel.name}</span>
    <span className="channels-management__status">Inactive</span>
    <button type="button" className="button button--gray" onClick={() => onEdit(channel)}>Edit</button>
    <button
      type="button"
      className="button button--blue"
      onClick={() => onToggleActive(channel)}
      disabled={togglingId === channel.id}
    >
      {togglingId === channel.id ? "Saving..." : "Activate"}
    </button>
  </li>
);

interface SortableChannelListProps {
  channels: IManagedChannel[];
  inactiveChannels?: IManagedChannel[];
  togglingId: number | null;
  onEdit: (channel: IManagedChannel) => void;
  onToggleActive: (channel: IManagedChannel) => void;
  onOrderChange: (orderedIds: number[]) => void;
}

const SortableChannelList: React.FC<SortableChannelListProps> = ({
  channels,
  inactiveChannels = [],
  togglingId,
  onEdit,
  onToggleActive,
  onOrderChange
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = channels.findIndex((channel) => channel.id === active.id);
    const newIndex = channels.findIndex((channel) => channel.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(channels, oldIndex, newIndex);
    onOrderChange(reordered.map((channel) => channel.id));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={channels.map((channel) => channel.id)} strategy={verticalListSortingStrategy}>
        {channels.map((channel) => (
          <SortableRow
            key={channel.id}
            channel={channel}
            togglingId={togglingId}
            onEdit={onEdit}
            onToggleActive={onToggleActive}
          />
        ))}
      </SortableContext>
      {inactiveChannels.map((channel) => (
        <StaticInactiveRow
          key={channel.id}
          channel={channel}
          togglingId={togglingId}
          onEdit={onEdit}
          onToggleActive={onToggleActive}
        />
      ))}
    </DndContext>
  );
};

export default SortableChannelList;
